import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Modal } from "@/components/pcp";
import { getEmpresaAtiva } from "@/components/EmpresaSelector";
import { Plus, TrashSimple, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

function startOfWeek(d) {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const s = new Date(d);
  s.setDate(diff);
  s.setHours(0, 0, 0, 0);
  return s;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function iso(d) { return d.toISOString().slice(0, 10); }
const DIAS = ["SEGUNDA", "TERÇA", "QUARTA", "QUINTA", "SEXTA", "SÁBADO", "DOMINGO"];

export default function Programacao() {
  const nav = useNavigate();
  const [refDate, setRefDate] = useState(new Date());
  const [equipes, setEquipes] = useState([]);
  const [ordens, setOrdens] = useState([]);
  const [prog, setProg] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [fEmpresa, setFEmpresa] = useState(getEmpresaAtiva());
  const [fContrato, setFContrato] = useState("");
  const [fEquipe, setFEquipe] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ equipe_id: "", os_id: "", data: iso(new Date()), observacao: "" });

  const semanaInicio = useMemo(() => startOfWeek(refDate), [refDate]);
  const semanaFim = useMemo(() => addDays(semanaInicio, 6), [semanaInicio]);
  const dias = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(semanaInicio, i)), [semanaInicio]);

  const load = async () => {
    const params = new URLSearchParams({ inicio: iso(semanaInicio), fim: iso(semanaFim) });
    if (fEmpresa) params.set("empresa_id", fEmpresa);
    if (fContrato) params.set("contrato_id", fContrato);
    const [{ data: e }, { data: o }, { data: p }, { data: em }, { data: ct }] = await Promise.all([
      api.get("/equipes"), api.get("/ordens"), api.get(`/programacao?${params}`), api.get("/empresas"), api.get("/contratos"),
    ]);
    setEquipes(e); setOrdens(o.filter((x) => x.status !== "Cancelada" && x.status !== "Concluída")); setProg(p); setEmpresas(em); setContratos(ct);
  };

  useEffect(() => { load(); }, [semanaInicio.getTime(), fEmpresa, fContrato]); // eslint-disable-line react-hooks/exhaustive-deps
  const empMap = useMemo(() => Object.fromEntries(empresas.map((x) => [x.id, x])), [empresas]);
  const ctMap = useMemo(() => Object.fromEntries(contratos.map((x) => [x.id, x])), [contratos]);
  const equipesVis = useMemo(() => equipes.filter((eq) => !fEquipe || eq.id === fEquipe), [equipes, fEquipe]);

  const save = async () => {
    if (!form.equipe_id || !form.os_id || !form.data) { toast.error("Preencha equipe, OS e data"); return; }
    await api.post("/programacao", form);
    toast.success("Programação criada");
    setModal(false);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Remover?")) return;
    await api.delete(`/programacao/${id}`);
    load();
  };

  const osMap = useMemo(() => Object.fromEntries(ordens.map((o) => [o.id, o])), [ordens]);
  const today = iso(new Date());

  return (
    <>
      <PageHeader
        testid="page-programacao"
        title="Agenda Geral do Grupo"
        subtitle={`${semanaInicio.toLocaleDateString("pt-BR")} até ${semanaFim.toLocaleDateString("pt-BR")}`}
        actions={
          <>
            <input
              type="date"
              className="input"
              value={iso(refDate)}
              onChange={(e) => e.target.value && setRefDate(new Date(e.target.value + "T12:00:00"))}
              data-testid="prog-date-picker"
              style={{ minWidth: 160 }}
              title="Ir para uma data específica"
            />
            <button className="btn btn-outline" onClick={() => setRefDate((d) => addDays(d, -7))} data-testid="btn-prev-week"><ArrowLeft size={16} weight="bold" /> Anterior</button>
            <button className="btn btn-outline" onClick={() => setRefDate(new Date())} data-testid="btn-today">Hoje</button>
            <button className="btn btn-outline" onClick={() => setRefDate((d) => addDays(d, 7))} data-testid="btn-next-week">Próxima <ArrowRight size={16} weight="bold" /></button>
            <button className="btn btn-primary" onClick={() => setModal(true)} data-testid="btn-new-prog"><Plus size={16} weight="bold" /> Agendar</button>
          </>
        }
      />
      <div className="page-body">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "end", marginBottom: 12 }} data-testid="agenda-filtros">
          <div><div className="label">Entidade</div><select className="select" value={fEmpresa} onChange={(e) => setFEmpresa(e.target.value)} data-testid="agenda-filtro-empresa"><option value="">Todo o grupo</option>{empresas.map((x) => <option key={x.id} value={x.id}>{x.tipo === "consorcio" ? "Consórcio · " : ""}{x.nome}</option>)}</select></div>
          <div><div className="label">Contrato</div><select className="select" value={fContrato} onChange={(e) => setFContrato(e.target.value)} data-testid="agenda-filtro-contrato"><option value="">Todos</option>{contratos.filter((c) => c.status !== "Orçamento" && (!fEmpresa || c.empresa_executora_id === fEmpresa)).map((c) => <option key={c.id} value={c.id}>{c.numero} · {c.contratante}</option>)}</select></div>
          <div><div className="label">Equipe</div><select className="select" value={fEquipe} onChange={(e) => setFEquipe(e.target.value)} data-testid="agenda-filtro-equipe"><option value="">Todas</option>{equipes.map((x) => <option key={x.id} value={x.id}>{x.nome}</option>)}</select></div>
          {(fEmpresa || fContrato || fEquipe) && <button className="btn btn-ghost btn-sm" onClick={() => { setFEmpresa(""); setFContrato(""); setFEquipe(""); }} data-testid="agenda-limpar-filtros">Limpar filtros</button>}
          <div style={{ fontSize: "0.72rem", color: "#71717A", alignSelf: "center" }}>Agenda única do grupo — filtros apenas restringem a visualização.</div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <div className="week-grid" style={{ minWidth: 900 }}>
            <div className="week-head">Equipe</div>
            {dias.map((d, i) => (
              <div key={i} className="week-head">
                <div>{DIAS[i]}</div>
                <div className="font-mono" style={{ fontSize: "0.72rem", opacity: 0.85, marginTop: 2 }}>
                  {d.getDate().toString().padStart(2, "0")}/{(d.getMonth() + 1).toString().padStart(2, "0")}
                </div>
              </div>
            ))}

            {equipes.length === 0 && (
              <div className="week-cell" style={{ gridColumn: "1 / -1", textAlign: "center", padding: 24, color: "#71717A" }}>
                Cadastre equipes para começar a programar.
              </div>
            )}

            {equipesVis.map((eq) => (
              <div style={{ display: "contents" }} key={eq.id}>
                <div className="week-cell" style={{ display: "flex", alignItems: "center", fontWeight: 700 }}>{eq.nome}</div>
                {dias.map((d) => {
                  const key = iso(d);
                  const items = prog.filter((p) => p.equipe_id === eq.id && p.data === key);
                  return (
                    <div key={key} className={`week-cell ${key === today ? "today" : ""}`}>
                      {items.map((it) => {
                        const os = osMap[it.os_id];
                        const auto = it.auto_gerado;
                        return (
                          <div
                            key={it.id}
                            onClick={() => it.os_id && nav(`/ordens/${it.os_id}`)}
                            title="Clique para abrir a OS"
                            data-testid={`prog-card-${os?.numero || it.id}`}
                            style={{
                              padding: 6,
                              background: auto ? "#FFFCEA" : "#F4F4F5",
                              border: `1px solid ${auto ? "#FFCC00" : "#D4D4D8"}`,
                              borderLeft: `4px solid ${auto ? "#FFCC00" : "#0A0A0A"}`,
                              borderRadius: 3,
                              marginBottom: 4,
                              fontSize: "0.75rem",
                              cursor: "pointer",
                              transition: "background-color 0.15s ease, transform 0.1s ease",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = auto ? "#FFF7C2" : "#E4E4E7"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = auto ? "#FFFCEA" : "#F4F4F5"; }}
                          >
                            <div className="font-mono font-semibold">{os?.numero || "OS"}</div>
                            <div style={{ fontSize: "0.7rem", color: "#52525B" }}>{os?.contratante}</div>
                            {(it.empresa_executora_id || os?.empresa_executora_id) && <div style={{ fontSize: "0.62rem", color: "#1F3A5F", fontWeight: 700 }} data-testid={`prog-empresa-${it.id}`}>{empMap[it.empresa_executora_id || os?.empresa_executora_id]?.nome}{(it.contrato_id || os?.contrato_id) && ctMap[it.contrato_id || os?.contrato_id] ? ` · CT ${ctMap[it.contrato_id || os?.contrato_id].numero}` : ""}</div>}
                            {auto && <div style={{ fontSize: "0.62rem", color: "#78620F", marginTop: 2, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>Auto · OS</div>}
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ padding: 2, height: 20, marginTop: 4 }}
                              onClick={(e) => { e.stopPropagation(); remove(it.id); }}
                              title="Remover agendamento"
                            >
                              <TrashSimple size={10} weight="bold" color="#DC2626" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title="Agendar OS">
        <div style={{ display: "grid", gap: 12 }}>
          <F label="Equipe"><select className="select" value={form.equipe_id} onChange={(e) => setForm({ ...form, equipe_id: e.target.value })}><option value="">—</option>{equipes.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}</select></F>
          <F label="OS"><select className="select" value={form.os_id} onChange={(e) => setForm({ ...form, os_id: e.target.value })}><option value="">—</option>{ordens.map((o) => <option key={o.id} value={o.id}>{o.numero} · {o.contratante}</option>)}</select></F>
          <F label="Data"><input type="date" className="input" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} /></F>
          <F label="Observação"><input className="input" value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} /></F>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
          <button className="btn btn-outline" onClick={() => setModal(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} data-testid="btn-save-prog">Agendar</button>
        </div>
      </Modal>
    </>
  );
}
function F({ label, children }) { return <div><div className="label">{label}</div>{children}</div>; }
